"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.containerFacts = containerFacts;
exports.areaCategories = areaCategories;
exports.onSiteCategories = onSiteCategories;
const browser_1 = require("encamp-prisma/generated/browser");
const streamStateWasteCodes_1 = require("encamp-shared/dist/waste/streamStateWasteCodes");
const jurisdictions_1 = require("./rules/jurisdictions");
/** A container's facts, from its stream row and its own. Without a stream, its waste is unknown. */
function containerFacts(stream, container) {
    if (stream === null) {
        return { ...container };
    }
    return {
        classification: stream.classification,
        wasteKind: stream.wasteKind,
        stateWasteCodes: (0, streamStateWasteCodes_1.streamStateWasteCodes)(stream),
        ...container,
    };
}
function facilityFacts(facility) {
    return {
        state: facility.state,
        ...(facility.generatorStatus === null
            ? {}
            : { federalGeneratorStatus: facility.generatorStatus }),
        ...(facility.stateGeneratorStatus === null
            ? {}
            : { stateGeneratorStatus: facility.stateGeneratorStatus }),
    };
}
// An undecided category falls back to non-acute, the category of an unclassified container.
function categoryOf(rules, facts) {
    return rules.accumulationCategory(facts).value ?? "NON_ACUTE";
}
/**
 * Which of an area's totals a container's waste fills, resolved once per
 * area. A satellite area's totals are judged against its caps, so a
 * container fills the side whose caps include it. A central area has
 * no caps: its totals describe what is there and take the category alone.
 */
function areaCategories(facility, areaType) {
    const rules = (0, jurisdictions_1.jurisdictionRulesFor)(facility.state);
    if (areaType !== browser_1.WasteAccumulationAreaType.SATELLITE) {
        return (facts) => categoryOf(rules, facts);
    }
    const caps = rules
        .thresholdsFor({ facility: facilityFacts(facility), area: { type: areaType } })
        .flatMap((threshold) => (threshold.kind === "WASTE_AMOUNT" ? [threshold] : []));
    return (facts) => {
        const memberships = caps.map((cap) => ({
            categories: cap.includedCategories ?? [],
            included: rules.thresholdIncludes(cap, facts).value ?? null,
        }));
        const fills = (category) => memberships.some((m) => m.included === true && m.categories.includes(category));
        if (fills("ACUTE")) {
            return "ACUTE";
        }
        if (fills("NON_ACUTE")) {
            return "NON_ACUTE";
        }
        // Undecided membership is included, as the conservative reading of the caps.
        return memberships.some((m) => m.included === null) ? categoryOf(rules, facts) : "EXCLUDED";
    };
}
/**
 * Which on-site total a container's waste fills, resolved once per facility:
 * as displayed, by category alone, and as judged against the facility's
 * on-site thresholds. A facility with none (an LQG) has nothing to judge
 * against, so its judged side only drops what the episodic rule leaves out.
 */
function onSiteCategories(facility) {
    const rules = (0, jurisdictions_1.jurisdictionRulesFor)(facility.state);
    const thresholds = rules
        .thresholdsFor({ facility: facilityFacts(facility) })
        .flatMap((threshold) => (threshold.kind === "WASTE_AMOUNT" ? [threshold] : []));
    return (facts) => {
        const displayed = categoryOf(rules, facts);
        if (displayed === "EXCLUDED") {
            return { displayed, judged: displayed };
        }
        if (thresholds.length === 0) {
            const episodic = rules.episodicExclusion(facts);
            return { displayed, judged: episodic.value === true ? "EXCLUDED" : displayed };
        }
        const memberships = thresholds
            .filter((threshold) => threshold.includedCategories?.includes(displayed))
            .map((threshold) => rules.thresholdIncludes(threshold, facts).value);
        // Undecided membership is included, as the conservative reading of the thresholds.
        const included = memberships.some((member) => member !== false);
        return { displayed, judged: included ? displayed : "EXCLUDED" };
    };
}
//# sourceMappingURL=accumulationCategories.js.map